/**
 * Shared helpers for uniform Tender Bidding Service SEO URLs:
 *   /tender-bidding-service/{type}/{slug}/
 *
 * Supported types: state, category, department, city, product
 */

import { indianStates } from "./locationConstants";

export const TENDER_BIDDING_SERVICE_PREFIX = "/tender-bidding-service";

export const TENDER_BIDDING_SERVICE_ROUTE_TYPES = {
  state: {
    queryParam: "state",
    typeLabel: "State",
    breadcrumbLabel: "State Wise",
  },
  category: {
    queryParam: "category",
    typeLabel: "Category",
    breadcrumbLabel: "Category Wise",
  },
  department: {
    queryParam: "departments",
    typeLabel: "Department",
    breadcrumbLabel: "Department Wise",
  },
  city: {
    queryParam: "city",
    typeLabel: "City",
    breadcrumbLabel: "City Wise",
  },
  product: {
    queryParam: "product",
    typeLabel: "Product",
    breadcrumbLabel: "Product Wise",
  },
};

const SEO_ACRONYMS = new Set([
  "ai",
  "api",
  "boq",
  "bhel",
  "cpwd",
  "cppp",
  "drdo",
  "gem",
  "iocl",
  "isro",
  "it",
  "msme",
  "nhai",
  "nhm",
  "nhpc",
  "ntpc",
  "ongc",
  "psu",
  "pwd",
  "sez",
]);

export const slugifyTenderBiddingValue = (value) =>
  String(value || "")
    .trim()
    .replace(/&/g, " and ")
    .replace(/\(([^)]+)\)/g, " $1 ")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

/**
 * Map display / humanized state names to the canonical value expected by
 * POST /tenders/advanced-filter (e.g. "Jammu Kashmir" → "Jammu-kashmir").
 * Other states keep their DB title-case labels (e.g. "Andhra Pradesh").
 */
export const normalizeStateNameForApi = (stateName) => {
  const raw = String(stateName || "").trim();
  if (!raw) return raw;

  const slug = slugifyTenderBiddingValue(raw);
  // DB stores this hyphenated spelling with capital J — must match exactly.
  if (slug === "jammu-kashmir" || slug === "jammu-and-kashmir") {
    return "Jammu-kashmir";
  }

  const matched = indianStates.find((s) => {
    const value = String(s?.value || "").trim().toLowerCase();
    const label = String(s?.label || "").trim();
    return (
      value === slug ||
      label.toLowerCase() === raw.toLowerCase() ||
      slugifyTenderBiddingValue(label) === slug
    );
  });

  if (matched) {
    return String(matched.label || matched.value || "").trim() || raw;
  }

  return raw;
};

const titleCaseSeoWord = (word) => {
  const clean = String(word || "").trim();
  if (!clean) return "";
  const lower = clean.toLowerCase();
  if (SEO_ACRONYMS.has(lower)) return lower.toUpperCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

export const humanizeTenderBiddingSlug = (slug, type) => {
  const parts = String(slug || "")
    .split("-")
    .map((part) => {
      try {
        return decodeURIComponent(part).trim();
      } catch {
        return part.trim();
      }
    })
    .filter(Boolean);

  if (parts.length === 0) return "";

  if (type === "department" && parts.length > 1) {
    const last = parts[parts.length - 1].toLowerCase();
    if (SEO_ACRONYMS.has(last) && last.length >= 3) {
      const name = parts
        .slice(0, -1)
        .map(titleCaseSeoWord)
        .join(" ");
      return `${name} (${last.toUpperCase()})`;
    }
  }

  return parts.map(titleCaseSeoWord).join(" ");
};

export const buildTenderBiddingServicePath = (type, value) => {
  const normalizedType = String(type || "").toLowerCase();
  const slug = slugifyTenderBiddingValue(value);

  if (!TENDER_BIDDING_SERVICE_ROUTE_TYPES[normalizedType] || !slug) {
    return "/search-list/";
  }

  return `${TENDER_BIDDING_SERVICE_PREFIX}/${normalizedType}/${slug}/`;
};

export const getTenderBiddingServiceContextFromPath = (pathname) => {
  const cleanPath = String(pathname || "")
    .split("?")[0]
    .split("#")[0]
    .replace(/\/+$/, "");

  const parts = cleanPath.split("/").filter(Boolean);

  if (parts[0] !== "tender-bidding-service") return null;

  const type = String(parts[1] || "").toLowerCase();
  const config = TENDER_BIDDING_SERVICE_ROUTE_TYPES[type];
  const slug = parts.slice(2).join("-");
  const value = humanizeTenderBiddingSlug(slug, type);

  if (!config || !slug || !value) return null;

  // State SEO pages: filter/API value must stay slug-form (e.g. jammu-kashmir),
  // while `value` stays humanized for titles/breadcrumbs.
  const filterValue =
    type === "state" ? normalizeStateNameForApi(slug) || value : value;

  return {
    type,
    slug,
    value,
    filterValue,
    queryParam: config.queryParam,
    typeLabel: config.typeLabel,
    breadcrumbLabel: config.breadcrumbLabel,
    path: buildTenderBiddingServicePath(type, value),
  };
};

export const getTenderBiddingServiceMeta = (context, extras = {}) => {
  if (!context) return null;

  const value = String(context.value || "").trim();
  const year = new Date().getFullYear();
  const stateExtra = String(extras.state || "").trim();

  let title = "";
  if (context.type === "state") {
    title = `Tenders in ${value} | Find and Track Opportunities`;
  } else if (context.type === "city") {
    title = `Tenders in ${value} | Find and Track Opportunities`;
  } else if (context.type === "category") {
    title = `${value} Tenders in India | Latest Opportunities`;
  } else if (context.type === "department") {
    title = `${value} Tenders | Notices and Bidding Support`;
  } else if (context.type === "product") {
    title = `${value} Tenders ${year} – Latest Government & e-Tenders`;
  } else {
    title = `${value} Tenders – Latest Government & e-Tenders`;
  }

  if (context.type === "city" || context.type === "state") {
    return {
      title: `Tenders in ${value} | Find and Track Opportunities`,
      description: `Explore government, municipal and PSU tenders in ${value}. Track deadlines, check eligibility and get bidding support with TenderApex.`,
      keywords: [
        `tenders in ${value}`,
        `${value} government tenders`,
        `${value} tender information`,
        `government tenders near ${value}`,
        `municipal tenders ${value}`,
        `PSU tenders ${value}`,
        `e procurement ${value}`,
        `tender alerts ${value}`,
        `bidding support ${value}`,
        `local tenders ${value}`,
      ].join(", "),
    };
  }

  if (context.type === "category") {
    return {
      title: `${value} Tenders in India | Latest Opportunities`,
      description: `Find ${value} tenders from government departments and PSUs across India. Understand eligibility, requirements and bid with confidence.`,
      keywords: [
        `${value} tenders`,
        `${value} government tenders India`,
        `${value} tender opportunities`,
        `${value} procurement tenders`,
        `${value} tender eligibility`,
        `GeM tenders ${value}`,
        `${value} bid requirements`,
        `${value} supply tenders`,
        `${value} tender alerts`,
      ].join(", "),
    };
  }

  if (context.type === "department") {
    return {
      title: `${value} Tenders | Notices and Bidding Support`,
      description: `Track tender notices from ${value}. Review eligibility, deadlines and documents, and get support preparing your bid with TenderApex.`,
      keywords: [
        `${value} tenders`,
        `${value} tender notices`,
        `${value} eprocurement`,
        `tenders issued by ${value}`,
        `${value} bid opportunities`,
        `${value} tender eligibility`,
        `${value} NIT`,
        `${value} corrigendum`,
        `${value} tender alerts`,
      ].join(", "),
    };
  }

  const relation =
    context.type === "product" ? `for ${value}` : `in ${value}`;

  return {
    title,
    description: `Get expert Tender Bidding Service ${relation} for documentation, eligibility checks, and online submission. Talk to a tender consultant today.`,
    keywords: `${title}, Tender Apex`,
  };
};

const splitCsv = (raw) =>
  String(raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * If search params represent a single SEO-friendly filter, return the SEO path.
 * Otherwise return null (caller should keep /search-list?...).
 */
export const tryBuildTenderBiddingServiceUrlFromParams = (paramsInput) => {
  const params =
    paramsInput instanceof URLSearchParams
      ? paramsInput
      : new URLSearchParams(paramsInput || "");

  const stateValues = splitCsv(params.get("state"));
  const categoryValues = splitCsv(
    params.get("category") || params.get("subCategory"),
  );
  const departmentValues = splitCsv(
    params.get("departments") || params.get("authority"),
  );
  const cityValues = splitCsv(params.get("city"));
  const productValues = splitCsv(params.get("product"));

  const hasExtra = [...params.keys()].some((key) => {
    const k = String(key || "").toLowerCase();
    return ![
      "state",
      "category",
      "subcategory",
      "departments",
      "authority",
      "city",
      "product",
    ].includes(k);
  });

  if (hasExtra) return null;

  const groups = [
    { type: "state", values: stateValues },
    { type: "category", values: categoryValues },
    { type: "department", values: departmentValues },
    { type: "city", values: cityValues },
    { type: "product", values: productValues },
  ].filter((g) => g.values.length > 0);

  if (groups.length !== 1) return null;
  if (groups[0].values.length !== 1) return null;

  return buildTenderBiddingServicePath(groups[0].type, groups[0].values[0]);
};

/**
 * Prefer an SEO path when a single primary filter is present.
 * Extra params (industry, source, etc.) are preserved as query string on the SEO URL.
 * Multi-primary or empty → /search-list/?...
 */
export const resolveSearchListPath = (paramsInput) => {
  const params =
    paramsInput instanceof URLSearchParams
      ? new URLSearchParams(paramsInput)
      : new URLSearchParams(paramsInput || "");

  const stateValues = splitCsv(params.get("state"));
  const categoryValues = splitCsv(
    params.get("category") || params.get("subCategory"),
  );
  const departmentValues = splitCsv(
    params.get("departments") || params.get("authority"),
  );
  const cityValues = splitCsv(params.get("city"));
  const productValues = splitCsv(params.get("product"));

  const groups = [
    {
      type: "state",
      values: stateValues,
      keys: ["state"],
    },
    {
      type: "category",
      values: categoryValues,
      keys: ["category", "subCategory"],
    },
    {
      type: "department",
      values: departmentValues,
      keys: ["departments", "authority"],
    },
    {
      type: "city",
      values: cityValues,
      keys: ["city"],
    },
    {
      type: "product",
      values: productValues,
      keys: ["product"],
    },
  ].filter((g) => g.values.length > 0);

  const singlePrimary =
    groups.length === 1 && groups[0].values.length === 1 ? groups[0] : null;

  if (singlePrimary) {
    const rest = new URLSearchParams(params);
    singlePrimary.keys.forEach((key) => rest.delete(key));
    const path = buildTenderBiddingServicePath(
      singlePrimary.type,
      singlePrimary.values[0],
    );
    const qs = rest.toString();
    return qs ? `${path}?${qs}` : path;
  }

  const qs = params.toString();
  return qs ? `/search-list/?${qs}` : "/search-list/";
};
